import { useEffect, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../api/staff'
import type {
  AuthSession,
  StaffCommunicationsData,
  StaffDashboardData,
  StaffGiftCertificate,
  StaffGiftCertificateFormInput,
  StaffGiftCertificatesData,
  StaffOperationsData,
  StaffPatientsListData,
  StaffScheduleData,
  StaffUsersData,
} from '../types/plans'
import type { StaffUsersCatalogResponse, StaffUsersWriteUser } from '@unified-lk/api-contracts'
import { StaffShell } from './StaffShell'
import { StaffCommunicationsSection } from './sections/StaffCommunicationsSection'
import { StaffDashboardSection } from './sections/StaffDashboardSection'
import { StaffGiftCertificatesSection } from './sections/StaffGiftCertificatesSection'
import { StaffOperationsFeedSection } from './sections/StaffOperationsFeedSection'
import { StaffScheduleSection } from './sections/StaffScheduleSection'
import { StaffUsersSection } from './sections/StaffUsersSection'
import { StaffPromotionsWorkspace } from './sections/promotions/StaffPromotionsWorkspace'
import { StaffReportsSection } from './sections/StaffReportsSection'
// 03.05.26: модуль «Документы» — рассрочка / налоговая справка через MIS proxy.
import { StaffDocumentsWorkspace } from './documents/StaffDocumentsWorkspace'
// 20.05.26: медиа-репозиторий.
import { StaffMediaSection } from './sections/StaffMediaSection'

interface NavigationItem {
  key: string
  label: string
}

type StaffViewKey =
  | 'dashboard'
  | 'schedule'
  | 'communications'
  | 'users'
  | 'gift-certificates'
  | 'payments'
  | 'diagnocat'
  | 'promotions'
  | 'content'
  | 'analytics'
  | 'reports'
  | 'documents'
  | 'media'

export function StaffOperationsWorkspace({
  bootstrap,
  navigation,
  currentView,
  onNavigate,
  session,
  onLogout,
}: {
  bootstrap: TenantBootstrapModel
  navigation: NavigationItem[]
  currentView: StaffViewKey
  onNavigate: (view: string) => void
  session: AuthSession
  onLogout: () => Promise<void> | void
}) {
  const [dashboard, setDashboard] = useState<StaffDashboardData | null>(null)
  const [schedule, setSchedule] = useState<StaffScheduleData | null>(null)
  const [communications, setCommunications] = useState<StaffCommunicationsData | null>(null)
  const [users, setUsers] = useState<StaffUsersData | null>(null)
  const [giftCertificates, setGiftCertificates] = useState<StaffGiftCertificatesData | null>(null)
  const [operations, setOperations] = useState<StaffOperationsData | null>(null)
  const [loadingMap, setLoadingMap] = useState<Record<StaffViewKey, boolean>>(
    // 'constructor' key conflicts with Object.prototype.constructor typing, use assertion
    {
      dashboard: false,
      schedule: false,
      communications: false,
      users: false,
      'gift-certificates': false,
      payments: false,
      diagnocat: false,
      promotions: false,
      content: false,
      analytics: false,
      reports: false,
      documents: false,
      media: false,
      'payment-links': false,
      constructor: false,
      plans: false,
      patients: false,
    } as Record<StaffViewKey, boolean>,
  )
  const [errorMap, setErrorMap] = useState<Partial<Record<StaffViewKey, string>>>({})
  const [usersCatalog, setUsersCatalog] = useState<{ permissions: StaffUsersCatalogResponse['permissions']; roles: StaffUsersCatalogResponse['roles'] } | null>(null)
  const canIssueSecretLinks = session.user.permissions.includes('secret_login') || session.user.permissions.includes('users')
  const canManageUsers = session.user.permissions.includes('users')
  const autoOpenSecretLinks = session.user.permissions.includes('users')

  useEffect(() => {
    void loadCurrentView(currentView)
  }, [bootstrap.apiBase, currentView, session.accessToken])

  async function loadCurrentView(view: StaffViewKey, force = false) {
    setLoadingMap((previous) => ({ ...previous, [view]: true }))
    setErrorMap((previous) => ({ ...previous, [view]: undefined }))

    try {
      if (view === 'dashboard' && (force || !dashboard)) {
        setDashboard(await staffApi.getDashboard(bootstrap, session))
      }

      if (view === 'schedule' && (force || !schedule)) {
        setSchedule(await staffApi.getSchedule(bootstrap, session))
      }

      if (view === 'communications' && (force || !communications)) {
        setCommunications(await staffApi.getCommunications(bootstrap, session))
      }

      if (view === 'users' && (force || !users)) {
        setUsers(await staffApi.getUsers(bootstrap, session))
      }

      if (view === 'gift-certificates' && (force || !giftCertificates)) {
        setGiftCertificates(await staffApi.getGiftCertificates(bootstrap, session))
      }

      if (
        ['payments', 'diagnocat', 'content', 'analytics'].includes(view)
        && (force || !operations)
      ) {
        setOperations(await staffApi.getOperationsFeed(bootstrap, session))
      }
    } catch (reason) {
      setErrorMap((previous) => ({
        ...previous,
        [view]: reason instanceof Error ? reason.message : 'Не удалось загрузить раздел.',
      }))
    } finally {
      setLoadingMap((previous) => ({ ...previous, [view]: false }))
    }
  }

  async function previewGiftCertificate(input: StaffGiftCertificateFormInput): Promise<StaffGiftCertificate> {
    return staffApi.previewGiftCertificate(bootstrap, session, input)
  }

  async function issueGiftCertificate(input: StaffGiftCertificateFormInput): Promise<StaffGiftCertificate> {
    const issued = await staffApi.issueGiftCertificate(bootstrap, session, input)
    await loadCurrentView('gift-certificates', true)

    return issued
  }

  async function searchPatients(search: string): Promise<StaffPatientsListData['patients']> {
    if (search.trim().length < 2) {
      return []
    }

    const payload = await staffApi.getPatientsList(bootstrap, session, {
      page: 1,
      perPage: 10,
      search: search.trim(),
    })

    return payload.patients
  }

  async function issuePatientSecretLink(patientId: number): Promise<string> {
    const link = await staffApi.createSecretLoginLink(bootstrap, session, {
      targetRole: 'patient',
      targetUserId: patientId,
      ttlMinutes: 15,
    })

    return link.url
  }

  async function issueStaffSecretLink(userId: number): Promise<string> {
    const link = await staffApi.createSecretLoginLink(bootstrap, session, {
      targetRole: 'staff',
      targetUserId: userId,
      ttlMinutes: 15,
    })

    return link.url
  }

  async function loadUsersCatalog() {
    if (usersCatalog) return
    try {
      const catalog = await staffApi.getUsersCatalog(bootstrap, session)
      setUsersCatalog(catalog)
    } catch {
      // ignore catalog errors; UI will fall back to free-form role
    }
  }

  async function createStaffUser(payload: Omit<StaffUsersWriteUser, 'id'>): Promise<void> {
    await staffApi.createUser(bootstrap, session, { ...payload, full_name: payload.full_name ?? payload.display_name ?? '' })
    await loadCurrentView('users', true)
  }

  async function updateStaffUser(userId: number, patch: Partial<Omit<StaffUsersWriteUser, 'id'>>): Promise<void> {
    await staffApi.updateUser(bootstrap, session, userId, patch)
    await loadCurrentView('users', true)
  }

  async function setStaffUserActive(userId: number, isActive: boolean): Promise<void> {
    await staffApi.setUserActive(bootstrap, session, userId, isActive)
    await loadCurrentView('users', true)
  }

  function renderCurrentView() {
    if (currentView === 'dashboard') {
      return (
        <StaffDashboardSection
          data={dashboard}
          loading={loadingMap.dashboard}
          error={errorMap.dashboard ?? null}
          tenant={bootstrap.tenant}
          onRetry={() => void loadCurrentView('dashboard', true)}
          onNavigate={onNavigate}
        />
      )
    }

    if (currentView === 'schedule') {
      return (
        <StaffScheduleSection
          bootstrap={bootstrap}
          session={session}
          data={schedule}
          loading={loadingMap.schedule}
          error={errorMap.schedule ?? null}
          onRetry={() => void loadCurrentView('schedule', true)}
          canIssueSecretLinks={canIssueSecretLinks}
          autoOpenSecretLinks={autoOpenSecretLinks}
          onIssuePatientSecretLink={issuePatientSecretLink}
        />
      )
    }

    if (currentView === 'communications') {
      return (
        <StaffCommunicationsSection
          data={communications}
          loading={loadingMap.communications}
          error={errorMap.communications ?? null}
          onRetry={() => void loadCurrentView('communications', true)}
        />
      )
    }

    if (currentView === 'users') {
      return (
        <StaffUsersSection
          data={users}
          loading={loadingMap.users}
          error={errorMap.users ?? null}
          onRetry={() => void loadCurrentView('users', true)}
          canIssueSecretLinks={canIssueSecretLinks}
          onIssueStaffSecretLink={issueStaffSecretLink}
          canManageUsers={canManageUsers}
          catalog={usersCatalog}
          onLoadCatalog={loadUsersCatalog}
          onCreateUser={createStaffUser}
          onUpdateUser={updateStaffUser}
          onSetUserActive={setStaffUserActive}
        />
      )
    }

    if (currentView === 'promotions') {
      return (
        <StaffPromotionsWorkspace
          bootstrap={bootstrap}
          session={session}
        />
      )
    }

    if (currentView === 'reports') {
      return (
        <StaffReportsSection
          bootstrap={bootstrap}
          session={session}
        />
      )
    }

    // 03.05.26: модуль «Документы» — proxy в MIS Documents API.
    if (currentView === 'documents') {
      return (
        <StaffDocumentsWorkspace
          bootstrap={bootstrap}
          session={session}
          permissions={session.user?.permissions ?? undefined}
        />
      )
    }

    // 20.05.26: медиа-репозиторий staff-LK.
    if (currentView === 'media') {
      return (
        <StaffMediaSection
          bootstrap={bootstrap}
          session={session}
        />
      )
    }

    if (['payments', 'diagnocat', 'content', 'analytics'].includes(currentView)) {
      return (
        <StaffOperationsFeedSection
          data={operations}
          loading={loadingMap[currentView]}
          error={errorMap[currentView] ?? null}
          activeSlug={currentView}
          onRetry={() => void loadCurrentView(currentView, true)}
        />
      )
    }

    return (
      <StaffGiftCertificatesSection
        data={giftCertificates}
        loading={loadingMap['gift-certificates']}
        error={errorMap['gift-certificates'] ?? null}
        onRetry={() => void loadCurrentView('gift-certificates', true)}
        onPreview={previewGiftCertificate}
        onIssue={issueGiftCertificate}
        onSearchPatients={searchPatients}
      />
    )
  }

  return (
    <StaffShell
      title="Операционный кабинет"
      subtitle=""
      navigation={navigation}
      currentView={currentView}
      onNavigate={onNavigate}
      session={session}
      bootstrapTenant={bootstrap.tenant}
      onRefresh={() => void loadCurrentView(currentView, true)}
      onLogout={() => void onLogout()}
    >
      {renderCurrentView()}
    </StaffShell>
  )
}
